/**
 * Singleton class for setting opened windows
 * and generating css classes with z-index setting
 * for popupSettings of window's components.
 * It supports dialog and warning windows for now
 * @author Zarko Radojevic
 */

import _ from 'lodash';
import { removeAt } from '../Util';
import uuid from 'uuid-random';
import { Service } from '../../dispatcher/services/Service';
import { DialogConfig } from '../../dispatcher/Types';

export enum InsensitiveWindowTypes {
  dialog = 'dialog',
  warning = 'warning',
  widget = 'widget'
}

export type WindowOptions = {
  index?: number,
  type?: string,
  previousFocus?: boolean | string,
  configWarningDialog?: DialogConfig
}

export type AddAllWindowOptions = {
  type: InsensitiveWindowTypes,
  previousFocus: boolean | string,
  configWarningDialog: DialogConfig
}

export type RemoveAllWindowOptions = {
  type: InsensitiveWindowTypes,
  index: number
}
/**
 * Kendo wrapper class for all elements that contains animation.
 */
export const ANIMATION_CONTAINER_CLASS: string = 'k-animation-container';

export class WindowManager extends Service {
  private static _instance: WindowManager;

  public static get Instance() {
    return this._instance || (this._instance = new this());
  }

  private ANIMATION_CONTAINER_STYLE_SHEET: any[] = [];
  private ANIMATION_CONTAINER_CSS_TEXT: string[];
  public readonly DIALOG_CONTAINER_ID: any = {
    warning: 'warning-modal-root',
    dialog: 'dialog-modal-root',
    widget: 'widgets'
  };
  public zIndexClsPrefix: string = 'dispatcher-z-index-';
  private windowContainerIdSuffix: string = '-modal-root';
  private zIndexWidget: number = 11000;
  private zIndexDialog: number = 12000;
  private zIndexWarning: number = 13000;
  // @ts-ignore
  private activeWindow: any;
  private openedWindows: any[] = [];
  public openedMultiWindows: any = {};
  private styleObj: any;
  public openedMultiWindowsSortedInOrder: AddAllWindowOptions[] = [];
  public previousFocusToDo: boolean | string = '';

  constructor() {
    super();
    this.createWindowContainers();
    this.createStyleObject();
  }

  /**
   * Adds style sheet rule
   * @param {StyleSheet} sheet
   * @param {String} clsName
   * @param {String} rules
   */
  private addStyleSheetRule = (sheet: any, clsName: string, rules: string) => {
    if (!(sheet || {}).insertRule) {
      sheet.addRule(clsName, rules);
    } else {
      sheet.insertRule(`${clsName}{${rules}}`, 0);
    }
  };

  /**
   * Removes window from window list
   * @param {String} type Type of window . There can be Dialog, WarningDialog...
   * @param options
   */
  public closeWindow(type: string, options: WindowOptions = {}) {
    const dialogType = this.getDialogType(type);
    const openedMultiWindowCounter = _.get(this.openedMultiWindows, type);
    _.set(this.openedMultiWindows, type, openedMultiWindowCounter - 1);
    if (openedMultiWindowCounter === 1) {
      this.openedWindows = this.openedWindows.filter((item: string) => item !== dialogType);
    }
    if (dialogType !== 'warning') {
      const len = this.openedWindows.length;
      if (len) {
        this.setAnimationContainerCssRule(this.getZIndexByDialogType(this.openedWindows[len - 1]));
      } else {
        this.setAnimationContainerCssRule();
      }
    }
    const extendedOptions: any = { ...options, type: dialogType };
    this.removeOpenedMultiWindowSortedInOrder(extendedOptions);
  }

  /**
   * Creates window containers (For WarningDialog, Dialog and Widget)
   */
  private createWindowContainers = () => {
    setTimeout(() => {
      if (!document.querySelector(`#${this.DIALOG_CONTAINER_ID.warning}`)) {
        let div;
        for (let id in this.DIALOG_CONTAINER_ID) {
          if (this.DIALOG_CONTAINER_ID.hasOwnProperty(id)) {
            div = document.createElement('div');
            div.id = this.DIALOG_CONTAINER_ID[id];
            document.body.appendChild(div);
          }
        }
      }
    }, 1);
  };

  /**
   * Returns active window (dialog or warning) configuration
   */
  public getActiveWindow = (): AddAllWindowOptions => {
    let activeWindow = null;
    const count = this.getOpenedWindowsCount();
    if (count) {
      activeWindow = this.getAllOpenedWindows()[count - 1];
    }
    return activeWindow;
  };

  /**
   * Returns all opened windows array configuration
   */
  public getAllOpenedWindows = () => this.openedMultiWindowsSortedInOrder;

  /**
   * Returns number of opened windows
   */
  public getOpenedWindowsCount = () => this.openedMultiWindowsSortedInOrder.length;

  /**
   * Returns if active window is dialog
   */
  public isActiveWindowDialog = () => _.get(this.getActiveWindow(), 'type') === 'dialog';

  /**
   * Returns if active window is warning
   */
  public isActiveWindowWarning = () => _.get(this.getActiveWindow(), 'type') === 'warning';

  /**
   * Removes dialog/warning from all opened windows array that is sorted by the order of occurrence.
   * Sets current previous attribute value to previousFocusToDo property
   * @param options
   */
  private removeOpenedMultiWindowSortedInOrder = (options: RemoveAllWindowOptions) => {
    let { type, index } = options; // index has value of -1 when all windows are closed
    const isCloseAll: boolean = index === -1;
    let removedTypeIndex = -1;
    let removedIndex = -1;
    const len = this.openedMultiWindowsSortedInOrder.length;
    for (let ind = 0; ind < len; ind++) {
      if (this.openedMultiWindowsSortedInOrder[ind].type === type) {
        removedTypeIndex++;
      }
      if (removedTypeIndex === index && !isCloseAll) {
        removedIndex = ind;
        break;
      }
    }
    let removeIndexes: number[] = [];
    if (removedIndex === -1 && isCloseAll) {
      if (options.type) {
        let firstWindowType = _.find(this.openedMultiWindowsSortedInOrder, {
          type: options.type
        });
        let firstIndex = _.findIndex(this.openedMultiWindowsSortedInOrder, firstWindowType);
        if (firstIndex !== -1) {
          let next: number = firstIndex;
          while (next < len) {
            if (this.openedMultiWindowsSortedInOrder[next].type !== options.type) {
              this.openedMultiWindowsSortedInOrder[next].previousFocus = this.openedMultiWindowsSortedInOrder[firstIndex].previousFocus;
              firstWindowType = _.find(this.openedMultiWindowsSortedInOrder, {
                type: options.type
              }, next);
              if (firstWindowType) {
                firstIndex = _.findIndex(this.openedMultiWindowsSortedInOrder, firstWindowType);
              } else {
                break;
              }
              next = firstIndex;
            } else {
              removeIndexes.push(next);
            }
            next++;
          }
        }
      } else {
        removedIndex = 0;
        this.previousFocusToDo = this.openedMultiWindowsSortedInOrder.length ? this.openedMultiWindowsSortedInOrder[removedIndex].previousFocus : '';
      }
    } else if (removedIndex < len - 1) {
      this.openedMultiWindowsSortedInOrder[removedIndex + 1].previousFocus = this.openedMultiWindowsSortedInOrder[removedIndex].previousFocus;
      this.previousFocusToDo = '';
    } else {
      this.previousFocusToDo = this.openedMultiWindowsSortedInOrder[removedIndex].previousFocus;
    }
    if (removeIndexes.length > 0) {
      let removeIndexesLength = removeIndexes.length;
      removeIndexes = removeIndexes.sort((a: number, b: number) => b - a);
      for (let i = 0; i < removeIndexesLength; i++) {
        removeAt(this.openedMultiWindowsSortedInOrder, removeIndexes[i]);
      }
      this.previousFocusToDo = _.last(this.openedMultiWindowsSortedInOrder)?.previousFocus || '';
    } else {
      removeAt(this.openedMultiWindowsSortedInOrder, removedIndex);
    }
  };

  /**
   * Sets focus to previous focused element and removes attribute from that element
   */
  public setPreviousFocusToView = () => {
    if (this.previousFocusToDo) {
      const previousFocusElement = document.querySelectorAll(`[previousWindowFocus="${this.previousFocusToDo}"]`)[0];
      if (previousFocusElement) {
        previousFocusElement.removeAttribute('previousWindowFocus');
        // @ts-ignore
        previousFocusElement.focus();
        setTimeout(() => {
          if (document.activeElement !== previousFocusElement) {
            // @ts-ignore
            previousFocusElement.focus();
          }
        }, 1);
      }
    }
  };

  /**
   * Creates style dom object
   */
  private createStyleObject() {
    this.styleObj = document.createElement('style');
    this.styleObj.type = 'text/css';
    document.getElementsByTagName('head')[0].appendChild(this.styleObj);
  }

  /**
   * Generates css class and removes previous with z-index based on window type
   * @param {String} type Type of window . There can be dialog, warning, widget ...
   * @param {number} zIndex
   */
  private generateZIndexCssClass(type: string, zIndex: number) {
    const clsName = `.${this.zIndexClsPrefix}${type}`;
    const rules = `z-index: ${zIndex} !important`;
    // const rules = `z-index: ${zIndex}`;
    this.removeStyleSheetRule(clsName);
    if (!(this.styleObj.sheet || {}).insertRule) {
      (this.styleObj.styleSheet || this.styleObj.sheet).addRule(clsName, rules);
    } else {
      this.styleObj.sheet.insertRule(`${clsName}{${rules}}`, 0);
    }
  }

  /**
   * Returns converted animation container css rules for all style sheets. Z-index is converted based on type of dialog that is opened
   * @param {Number} zIndex
   */
  private getAnimationContainerCssRule = (zIndex: number = null) => {
    let ruleArr = this.ANIMATION_CONTAINER_CSS_TEXT;
    if (zIndex !== null) {
      ruleArr = ruleArr.map((rule) => {
        let rules: any[] = rule.split(';');
        rules = rules.map((item: string) => {
          if (item.search('z-index') !== -1) {
            item = `z-index: ${zIndex} !important`;
            // item = `z-index: ${zIndex}`;
          }
          return item;
        });
        rule = rules.join(';');
        return rule;
      });
    }
    return ruleArr;
  };

  /**
   * Returns style sheets that contains animation container css class
   */
  private getAnimationContainerStyleSheet = () => {
    if (!this.ANIMATION_CONTAINER_STYLE_SHEET.length) {
      this.ANIMATION_CONTAINER_STYLE_SHEET = this.getStyleSheet(ANIMATION_CONTAINER_CLASS);
    }
    return this.ANIMATION_CONTAINER_STYLE_SHEET;
  };

  /**
   * Returns short name of dialogs class name that to be use as dialog type
   * @param {String} type
   */
  private getDialogType = (type: string) => {
    let dialogType: any;
    switch (type) {
      case 'Dialog':
        dialogType = 'dialog';
        break;
      case 'WarningDialog':
        dialogType = 'warning';
        break;
      default:
        dialogType = 'widget';
        break;
    }
    return dialogType;
  };

  /**
   * Returns and sets style sheets on document and sets default animation container style rules
   * @param {String} selector Name of css class plus '.' as prefix
   */
  private getStyleSheet(selector: any) {
    let styleSheet: any = [];
    this.ANIMATION_CONTAINER_CSS_TEXT = [];
    for (let i = 0; i < document.styleSheets.length; i++) {
      let sheet: any = document.styleSheets[i];
      let rules = _.get(sheet, 'rules') || _.get(sheet, 'cssRules') || [];
      for (let j = 0; j < rules.length; j++) {
        let rule = rules[j];
        if (rule.selectorText === `.${selector}` && rule.style && rule.style.cssText && rule.style.cssText.search('z-index') !== -1) {
          this.ANIMATION_CONTAINER_CSS_TEXT.push(rule.style.cssText);
          styleSheet.push(sheet);
        }
      }
    }
    return styleSheet;
  }

  /**
   * Returns z-index based on dialog type
   * @param {String} type
   */
  private getZIndexByDialogType = (type: string): number => {
    let zIndex: number;
    switch (type) {
      case 'widget':
        zIndex = this.zIndexWidget;
        break;
      case 'dialog':
        zIndex = this.zIndexDialog;
        break;
      case 'warning':
        zIndex = this.zIndexWarning;
        break;
      default:
        zIndex = 0;
        break;
    }
    return zIndex;
  };

  /**
   * Adds window to window list
   * @param {String} type Type of window . There can be dialog, warning, widget ...
   * @param options
   */
  public openWindow(type: string, options: WindowOptions = {}) {
    const dialogType = this.getDialogType(type);
    const openedMultiWindowCounter = _.get(this.openedMultiWindows, type);
    if (!openedMultiWindowCounter) {
      _.set(this.openedMultiWindows, type, 1);
      this.openedWindows.push(dialogType);
    } else {
      _.set(this.openedMultiWindows, type, openedMultiWindowCounter + 1);
    }
    if (dialogType !== 'warning') {
      this.setAnimationContainerCssRule(this.getZIndexByDialogType(dialogType));
      this.setStyleOtherVisiblePopups();
    }
    const extendedOptions: any = { ...options, type: dialogType };
    this.addOpenedMultiWindowSortedInOrder(extendedOptions);
  }

  /**
   * Adds dialog/warning to all opened windows array that is sorted by the order of occurrence.
   * Sets to previous focused element flag as attribute in order to find that after close dialog/warning.
   * @param options
   */
  private addOpenedMultiWindowSortedInOrder = (options: AddAllWindowOptions) => {
    let { previousFocus, type, configWarningDialog } = options;
    if (previousFocus) {
      if (_.isBoolean(previousFocus)) {
        previousFocus = uuid();
      }
      document.activeElement.setAttribute('previousWindowFocus', previousFocus);
    }
    this.openedMultiWindowsSortedInOrder.push({
      type,
      previousFocus,
      configWarningDialog
    });
  };

  /**
   * Removes style sheet rule
   * @param {String} rule Name of css class(With '.' prefix)
   * @param {StyleSheet} sheet
   */
  private removeStyleSheetRule(rule: string, sheet?: any) {
    if (!sheet) {
      let styleTag = this.styleObj;
      sheet = styleTag.sheet ? styleTag.sheet : styleTag.styleSheet;
    }
    if (sheet.cssRules) {
      // all browsers, except IE before version 9
      for (let i = 0; i < sheet.cssRules.length; i++) {
        if (sheet.cssRules[i].selectorText === rule) {
          sheet.deleteRule(i);
        }
      }
    } else {
      // Internet Explorer before version 9
      for (let i = 0; i < sheet.rules.length; i++) {
        if (sheet.rules[i].selectorText === rule) {
          sheet.removeRule(i);
        }
      }
    }
  }

  /**
   * Sets new animation container css rules and removes old rules
   * @param {Number} zIndex
   */
  private setAnimationContainerCssRule = (zIndex: number = null) => {
    if (this.getAnimationContainerStyleSheet().length) {
      const rules = this.getAnimationContainerCssRule(zIndex);
      const clsName = `.${ANIMATION_CONTAINER_CLASS}`;
      this.ANIMATION_CONTAINER_STYLE_SHEET.forEach((sheet: any, index) => {
        this.removeStyleSheetRule(clsName, sheet);
        this.addStyleSheetRule(sheet, clsName, rules[index]);
      });
    }
  };

  /**
   * Sets style to opened popup menus because animation panel style is changed
   */
  private setStyleOtherVisiblePopups = () => {
    let rule = ' z-index: 100 !important';
    const len = this.openedWindows.length;
    if (len > 1) {
      rule = ` z-index: ${this.getZIndexByDialogType(this.openedWindows[len - 2])} !important;`;
    }
    const visibleDocumentPopupNodes = document.querySelectorAll(`.${ANIMATION_CONTAINER_CLASS}`);
    visibleDocumentPopupNodes.forEach((item: any) => {
      let style = item.getAttribute('style');
      if (style && style.search('z-index') === -1) {
        item.setAttribute('style', `${style.replace(rule, '')}${rule}`);
      }
    });
  };

  /**
   * Sets z-index to window wrappers and creates z-index popup css classes for all window types
   */
  public setWindowsZIndex() {
    this.setWindowZIndex('dialog');
    this.setWindowZIndex('warning');
    this.setWindowZIndex('widget');
  }

  /**
   * Creates dispatcher window css class with z-index setting for popup components
   * @param {String} type Type of window . There can be dialog, warning, widget ...
   */
  private setWindowZIndex(type: string) {
    const zIndex = this.getZIndexByDialogType(type);
    this.generateZIndexCssClass(type, zIndex);
    // @ts-ignore
    const dialogWrapper: any = document.getElementById(`${type}${this.windowContainerIdSuffix}`);
    if (dialogWrapper) {
      dialogWrapper.style.zIndex = zIndex;
    }
  }
}

const DispatcherWindowManager = WindowManager.Instance;
export default DispatcherWindowManager;
